import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthFetch } from '@/hooks/useAdmin';
import RichTextEditor from '@/components/RichTextEditor';
import MediaPickerModal from '@/components/MediaPickerModal';
import SortableGallery from '@/components/article/SortableGallery';
import CreatableSelect from 'react-select/creatable';
import { Save, Send, Clock, ChevronDown, ChevronRight, X, Image as ImageIcon, Loader2, ExternalLink, Quote, Share2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const LANGS = [
  { code: 'en', label: 'English' }, { code: 'de', label: 'Deutsch' },
  { code: 'es', label: 'Espanol' }, { code: 'fr', label: 'Francais' },
  { code: 'pt', label: 'Portugues' }, { code: 'zh', label: 'Zhongwen' },
  { code: 'ar', label: 'Arabic' }, { code: 'hi', label: 'Hindi' },
  { code: 'ru', label: 'Russian' }, { code: 'ja', label: 'Japanese' },
];

const selectStyles = {
  control: (b) => ({ ...b, backgroundColor: '#18181b', borderColor: '#3f3f46', minHeight: 38, '&:hover': { borderColor: '#52525b' } }),
  menu: (b) => ({ ...b, backgroundColor: '#18181b', border: '1px solid #3f3f46' }),
  option: (b, s) => ({ ...b, backgroundColor: s.isFocused ? '#27272a' : '#18181b', color: '#e4e4e7' }),
  input: (b) => ({ ...b, color: '#e4e4e7' }),
  multiValue: (b) => ({ ...b, backgroundColor: '#27272a' }),
  multiValueLabel: (b) => ({ ...b, color: '#e4e4e7' }),
  multiValueRemove: (b) => ({ ...b, '&:hover': { backgroundColor: '#ef4444', color: 'white' } }),
  placeholder: (b) => ({ ...b, color: '#52525b' }),
  singleValue: (b) => ({ ...b, color: '#e4e4e7' }),
};

export default function ArticleForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const authFetch = useAuthFetch();
  const isEdit = !!id;

  const [form, setForm] = useState({
    title_hr: '', slug: '', excerpt_hr: '', body_hr: '',
    cover_image_url: null, gallery_image_urls: [],
    category_id: '', tag_ids: [], status: 'draft', publish_at: '',
    is_premium: false,
    source_name: '', source_url: '', source_article_url: '',
    social_post: '',
    seo_title: '', meta_description: '', ai_keywords: '', image_prompt: '',
    title_translations: {}, excerpt_translations: {}, body_translations: {},
  });
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [mediaPickerMode, setMediaPickerMode] = useState('cover');
  const [editorInstance, setEditorInstance] = useState(null);
  const [showTranslations, setShowTranslations] = useState(false);
  const [activeLang, setActiveLang] = useState('en');
  // Pull-quote extractor state
  const [pqOpen, setPqOpen] = useState(false);
  const [pqLoading, setPqLoading] = useState(false);
  const [pqQuotes, setPqQuotes] = useState([]);
  const [pqError, setPqError] = useState('');
  // Branded share card state
  const [shareCardBusy, setShareCardBusy] = useState(false);
  // AI Edit Body modal state
  const [showAiEdit, setShowAiEdit] = useState(false);
  const [aiEditBusy, setAiEditBusy] = useState(false);
  const [aiEditModel, setAiEditModel] = useState('gemini-2.5-flash');
  const [aiEditProvider, setAiEditProvider] = useState('gemini');
  const [aiEditInstruction, setAiEditInstruction] = useState('');
  const [aiEditSystem, setAiEditSystem] = useState('');
  const [aiEditPreview, setAiEditPreview] = useState(null);

  async function runAiEditBody() {
    if (!form.body_hr || form.body_hr.length < 30) { toast.error('Sadržaj prekratak'); return; }
    if (!aiEditInstruction.trim()) { toast.error('Unesite uputu za AI'); return; }
    setAiEditBusy(true); setAiEditPreview(null);
    try {
      const r = await authFetch(`${API}/admin/articles/ai-edit-body`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          body_html: form.body_hr,
          instruction: aiEditInstruction,
          system_prompt: aiEditSystem || undefined,
          provider: aiEditProvider, model: aiEditModel, language: 'hr',
        }),
      });
      if (!r.ok) { toast.error('AI Edit nije uspio'); return; }
      const d = await r.json();
      setAiEditPreview(d.html || '');
    } catch (e) { toast.error('AI greška: ' + e.message); }
    finally { setAiEditBusy(false); }
  }

  function applyAiEdit() {
    if (!aiEditPreview) return;
    updateForm('body_hr', aiEditPreview);
    if (editorInstance) editorInstance.commands.setContent(aiEditPreview);
    setShowAiEdit(false); setAiEditPreview(null); setAiEditInstruction('');
    toast.success('AI izmjene primijenjene');
  }

  useEffect(() => { loadCategories(); loadTags(); loadAuthors(); if (isEdit) loadArticle(); }, [id]);

  async function loadArticle() {
    try {
      const res = await authFetch(`${API}/admin/articles/${id}`);
      if (res.ok) setForm(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  async function loadCategories() {
    try { const r = await authFetch(`${API}/admin/categories`); if (r.ok) { const d = await r.json(); setCategories(d.categories || []); } } catch (e) { console.error(e); }
  }

  async function loadTags() {
    try { const r = await authFetch(`${API}/admin/tags`); if (r.ok) { const d = await r.json(); setTags(d.tags || []); } } catch (e) { console.error(e); }
  }

  async function loadAuthors() {
    try {
      const r = await authFetch(`${API}/admin/authors`);
      if (r.ok) { const d = await r.json(); setAuthors(d.authors || []); }
    } catch (e) { console.error(e); }
  }

  function updateForm(field, value) { setForm(prev => ({ ...prev, [field]: value })); }

  function clientSlugify(text) {
    return text.toLowerCase().replace(/[cC]\u0327/g, 'c').replace(/[\u010D\u0107]/g, 'c').replace(/\u0161/g, 's').replace(/\u017E/g, 'z').replace(/\u0111/g, 'd')
      .replace(/[\u010C\u0106]/g, 'C').replace(/\u0160/g, 'S').replace(/\u017D/g, 'Z').replace(/\u0110/g, 'D')
      .toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  }

  function handleTitleChange(e) {
    const title = e.target.value;
    updateForm('title_hr', title);
    if (!isEdit || !form.slug) updateForm('slug', clientSlugify(title));
  }

  async function saveArticle(overrideStatus) {
    setSaving(true);
    const payload = { ...form };
    if (overrideStatus) payload.status = overrideStatus;
    // Only auto-stamp publish_at when transitioning to published AND the
    // admin hasn't already chosen a custom date. Otherwise respect their
    // input — including back-dated publish_at for arrangement on the
    // public timeline.
    if (overrideStatus === 'published' && !payload.publish_at) {
      payload.publish_at = new Date().toISOString();
    }
    ['id', 'author_id', 'views', 'created_at', 'updated_at'].forEach(k => delete payload[k]);
    if (!isEdit) delete payload.slug;

    try {
      const url = isEdit ? `${API}/admin/articles/${id}` : `${API}/admin/articles`;
      const res = await authFetch(url, { method: isEdit ? 'PUT' : 'POST', body: JSON.stringify(payload) });
      if (res.ok) {
        const data = await res.json();
        toast.success(overrideStatus === 'published' ? 'Objavljeno!' : 'Spremljeno!');
        if (!isEdit) navigate(`/admin/articles/${data.id}/edit`, { replace: true });
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Greska pri spremanju');
      }
    } catch (e) { toast.error('Greska: ' + e.message); }
    finally { setSaving(false); }
  }

  async function handleCreateTag(inputValue) {
    try {
      const res = await authFetch(`${API}/admin/tags`, { method: 'POST', body: JSON.stringify({ name: inputValue }) });
      if (res.ok) {
        const newTag = await res.json();
        setTags(prev => [...prev, newTag]);
        updateForm('tag_ids', [...form.tag_ids, newTag.id]);
      }
    } catch (e) { console.error(e); }
  }

  // Bulk-create tags from a comma-separated input. Reuses existing tags by
  // case-insensitive name match so admins can paste the same list twice
  // without ending up with duplicates.
  async function handleCreateManyTags(names) {
    const norm = (s) => s.trim().toLowerCase();
    const existingByName = new Map();
    tags.forEach(t => existingByName.set(norm(t.name), t));
    const idsToAdd = [];
    const created = [];
    for (const raw of names) {
      const name = raw.trim();
      if (!name) continue;
      const hit = existingByName.get(norm(name));
      if (hit) { idsToAdd.push(hit.id); continue; }
      try {
        const res = await authFetch(`${API}/admin/tags`, { method: 'POST', body: JSON.stringify({ name }) });
        if (res.ok) {
          const newTag = await res.json();
          created.push(newTag);
          existingByName.set(norm(newTag.name), newTag);
          idsToAdd.push(newTag.id);
        }
      } catch (e) { console.error(e); }
    }
    if (created.length) setTags(prev => [...prev, ...created]);
    if (idsToAdd.length) {
      updateForm('tag_ids', Array.from(new Set([...(form.tag_ids || []), ...idsToAdd])));
      toast.success(`Dodano oznaka: ${idsToAdd.length}`);
    }
  }

  function handleMediaSelect(value) {
    // value: string (single mode) or string[] (multi-select gallery mode)
    if (mediaPickerMode === 'cover') {
      // Cover is always single — defensive unwrap if an array arrives.
      const url = Array.isArray(value) ? value[0] : value;
      updateForm('cover_image_url', url);
    } else if (mediaPickerMode === 'gallery') {
      const incoming = Array.isArray(value) ? value : [value];
      const current = form.gallery_image_urls || [];
      // De-dupe against URLs already present in the gallery.
      const next = [...current, ...incoming.filter(u => u && !current.includes(u))];
      updateForm('gallery_image_urls', next);
      if (Array.isArray(value) && incoming.length > 1) {
        toast.success(`Dodano ${incoming.length} slika u galeriju`);
      }
    } else if (mediaPickerMode === 'inline' && editorInstance) {
      const url = Array.isArray(value) ? value[0] : value;
      editorInstance.chain().focus().setImage({ src: `${BACKEND}${url}` }).run();
    }
    setShowMediaPicker(false);
  }

  function updateTranslation(field, lang, value) {
    setForm(prev => ({ ...prev, [field]: { ...prev[field], [lang]: value } }));
  }

  const handleEditorReady = useCallback((editor) => { setEditorInstance(editor); }, []);

  // Ask the backend to extract 1–2 high-impact pull quotes from the current
  // body. Returns quickly (Gemini 2.5 Flash, ~2–4 s) so we keep the call
  // inline. We prefer the in-memory ``body_hr`` (unsaved edits count) over
  // whatever is persisted on the article doc.
  async function openPullQuotes() {
    if (!isEdit) { toast.error('Prvo spremi članak kao skicu pa pokreni ekstraktor.'); return; }
    const plain = (form.body_hr || '').replace(/<[^>]+>/g, '').trim();
    if (plain.length < 120) { toast.error('Članak je prekratak za pull-quote (min ~120 znakova).'); return; }
    setPqOpen(true); setPqLoading(true); setPqError(''); setPqQuotes([]);
    try {
      const r = await authFetch(`${API}/admin/articles/${id}/extract-pullquotes`, {
        method: 'POST',
        body: JSON.stringify({ body_hr: form.body_hr || '' }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        setPqError(d.detail || 'AI nije uspio vratiti citate.');
      } else {
        setPqQuotes(d.quotes || []);
      }
    } catch (e) {
      setPqError('Mrežna greška: ' + (e.message || ''));
    } finally { setPqLoading(false); }
  }

  function insertPullQuote(q) {
    if (!editorInstance) { toast.error('Editor nije spreman.'); return; }
    editorInstance.chain().focus().insertContent(q.html).insertContent('<p></p>').run();
    toast.success('Citat umetnut u članak.');
    setPqOpen(false);
  }

  async function regenerateShareCard() {
    if (!isEdit) { toast.error('Prvo spremi članak.'); return; }
    setShareCardBusy(true);
    try {
      const r = await authFetch(`${API}/admin/articles/${id}/regenerate-share-card`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        setForm(prev => ({ ...prev, share_card_url: d.share_card_url }));
        toast.success('Share card regeneriran.');
      } else {
        toast.error(d.detail || 'Generiranje nije uspjelo.');
      }
    } catch (e) { toast.error('Mrežna greška: ' + (e.message || '')); }
    finally { setShareCardBusy(false); }
  }

  if (loading) return <div className="flex items-center justify-center h-full"><Loader2 className="animate-spin text-zinc-500" /></div>;

  const tagOptions = tags.map(t => ({ value: t.id, label: t.name }));
  const selectedTagOptions = tagOptions.filter(o => (form.tag_ids || []).includes(o.value));

  return (
    <div className="p-6 max-w-4xl" data-testid="article-form">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">{isEdit ? 'Uredi clanak' : 'Nova objava'}</h1>
        <div className="flex gap-2">
          {isEdit && (
            <a href={`/admin/articles/${id}/versions`} className="flex items-center gap-1.5 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm rounded-md border border-zinc-700" data-testid="versions-link">
              Povijest izmjena
            </a>
          )}
          <button onClick={() => saveArticle()} disabled={saving} className="flex items-center gap-1.5 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm rounded-md border border-zinc-700" data-testid="save-draft-btn">
            <Save size={15} /> {saving ? 'Spremam...' : 'Spremi skicu'}
          </button>
          {form.status !== 'published' && (
            <button onClick={() => saveArticle('published')} disabled={saving} className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded-md" data-testid="publish-btn">
              <Send size={15} /> Objavi
            </button>
          )}
        </div>
      </div>

      {/* Imported-from callout: preserves one-click access to the original
          article URL even after the admin rewrites everything else. */}
      {(form.source_article_url || form.source_url) && (
        <div
          className="mb-6 flex items-start gap-3 rounded-lg border border-sky-700/40 bg-sky-950/30 px-4 py-3"
          data-testid="imported-from-banner"
        >
          <ExternalLink size={16} className="text-sky-300 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-sky-400 font-semibold mb-1">
              Uvezeno iz originalnog članka
            </div>
            <a
              href={form.source_article_url || form.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-sky-200 hover:text-sky-100 hover:underline break-all font-mono"
              data-testid="imported-from-link"
            >
              {form.source_article_url || form.source_url}
            </a>
            {form.source_name && (
              <div className="text-xs text-zinc-400 mt-0.5">Izvor: <span className="text-zinc-200">{form.source_name}</span></div>
            )}
          </div>
          <a
            href={form.source_article_url || form.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 text-[11px] px-3 py-1.5 rounded-md border border-sky-500/50 text-sky-200 hover:bg-sky-900/40 inline-flex items-center gap-1.5"
            data-testid="open-original-btn"
          >
            Otvori original
            <ExternalLink size={11} />
          </a>
        </div>
      )}

      <div className="space-y-5">
        {/* Title */}
        <div>
          <label className="block text-xs text-zinc-500 mb-1.5">Naslov (hrvatski) *</label>
          <input type="text" value={form.title_hr} onChange={handleTitleChange} className="w-full bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-sm text-zinc-200 outline-none focus:border-zinc-500" data-testid="title-input" placeholder="Unesite naslov clanka" />
        </div>

        {/* Slug */}
        <div>
          <label className="block text-xs text-zinc-500 mb-1.5">Slug</label>
          <input type="text" value={form.slug} onChange={e => updateForm('slug', e.target.value)} className="w-full bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-sm text-zinc-400 outline-none focus:border-zinc-500 font-mono" data-testid="slug-input" />
        </div>

        {/* Excerpt */}
        <div>
          <label className="block text-xs text-zinc-500 mb-1.5">Izvadak (hrvatski)</label>
          <textarea value={form.excerpt_hr} onChange={e => updateForm('excerpt_hr', e.target.value)} rows={2} className="w-full bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-sm text-zinc-200 outline-none focus:border-zinc-500 resize-y" data-testid="excerpt-input" />
        </div>

        {/* Body */}
        <div>
          <label className="block text-xs text-zinc-500 mb-1.5">Sadrzaj (hrvatski)</label>
          <div className="mb-2 flex gap-2 flex-wrap">
            <button type="button" onClick={() => {
              const pid = window.prompt('Unesite ID ankete (vidi /admin/polls)');
              if (pid && editorInstance) {
                editorInstance.chain().focus().insertContent(`<p>[poll:${pid.trim()}]</p>`).run();
              }
            }} className="text-xs px-2 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border border-zinc-700" data-testid="insert-poll-btn">
              + Umetni anketu
            </button>
            <button type="button" onClick={async () => {
              if (!form.excerpt_hr || form.excerpt_hr.length < 30) { toast.error('Izvadak je prekratak (min 30 znakova)'); return; }
              if (form.excerpt_hr.length > 500) { toast.error('Izvadak je predugačak (max 500 znakova za Enhance)'); return; }
              toast.info('AI proširuje izvadak… (Gemini 2.5 Flash)');
              try {
                const r = await authFetch(`${API}/admin/ai-enhance`, {
                  method: 'POST', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ excerpt: form.excerpt_hr, title: form.title_hr, target_words: 350 }),
                });
                if (r.ok) {
                  const d = await r.json();
                  updateForm('body_hr', d.html);
                  if (editorInstance) editorInstance.commands.setContent(d.html);
                  toast.success(`AI generirao ${d.chars} znakova`);
                } else toast.error('AI enhance neuspješan');
              } catch { toast.error('AI enhance neuspješan'); }
            }} className="text-xs px-2 py-1 rounded bg-gradient-to-r from-red-600 to-amber-500 hover:from-red-700 hover:to-amber-600 text-white font-bold border border-red-500" data-testid="ai-enhance-btn">
              ✨ AI Enhance izvadak → članak
            </button>
            <button type="button" onClick={openPullQuotes}
              disabled={!isEdit}
              className="text-xs px-2 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-100 border border-zinc-600 inline-flex items-center gap-1 disabled:opacity-50"
              title={isEdit ? 'AI izvlači 1–2 najupečatljivija citata i umeće ih kao istaknute pull-quote kartice.' : 'Prvo spremi članak.'}
              data-testid="pullquote-extract-btn">
              <Quote size={12} /> Pull-quote ekstraktor
            </button>
            <button type="button" onClick={() => setShowAiEdit(true)}
              className="text-xs px-2 py-1 rounded bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white font-bold border border-emerald-500 inline-flex items-center gap-1"
              title="AI uredi sadržaj prema uputi (Gemini Flash, model i prompt po izboru)"
              data-testid="ai-edit-body-btn">
              ✎ AI Uredi sadržaj
            </button>
          </div>
          <RichTextEditor
            key={id || 'new'}
            initialContent={form.body_hr}
            onChange={(html) => updateForm('body_hr', html)}
            onInsertImage={() => { setMediaPickerMode('inline'); setShowMediaPicker(true); }}
            onEditorReady={handleEditorReady}
          />
        </div>

        {/* Cover Image */}
        <div>
          <label className="block text-xs text-zinc-500 mb-1.5">Naslovna slika</label>
          <div className="flex items-center gap-3">
            {form.cover_image_url ? (
              <div className="relative">
                <img src={form.cover_image_url?.startsWith('http') ? form.cover_image_url : `${BACKEND}${form.cover_image_url}`} alt="Cover" className="w-32 h-20 object-cover rounded border border-zinc-700" />
                <button onClick={() => updateForm('cover_image_url', null)} className="absolute -top-2 -right-2 bg-zinc-800 border border-zinc-600 rounded-full p-0.5"><X size={12} /></button>
              </div>
            ) : null}
            <button onClick={() => { setMediaPickerMode('cover'); setShowMediaPicker(true); }} className="flex items-center gap-1.5 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-400 text-sm rounded-md border border-zinc-700" data-testid="pick-cover-btn">
              <ImageIcon size={15} /> {form.cover_image_url ? 'Promijeni' : 'Odaberi sliku'}
            </button>
          </div>
          {/* AI-generated image attribution. Visible only when this hero
              came from a generative model — helps the editor know whether
              to swap in a real photo before publishing. */}
          {form.image_source === 'ai_generated' && form.ai_image_model && (
            <p className="text-[11px] text-amber-400 mt-1.5" data-testid="article-ai-image-meta">
              ⚙ Slika generirana modelom <b>{form.ai_image_model}</b>
              {form.ai_image_provider ? ` (${form.ai_image_provider})` : ''}
            </p>
          )}
        </div>

        {/* Branded share card — automatski se generira pri objavi; admin
            ovdje može vidjeti pregled i forsirati regeneraciju nakon
            promjene naslova, autora ili kategorije. */}
        {isEdit && (
          <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-4" data-testid="share-card-section">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs text-zinc-400 inline-flex items-center gap-1.5">
                <Share2 size={12} /> Branded share card (Facebook / Twitter preview, 1200×630)
              </label>
              <button
                type="button"
                onClick={regenerateShareCard}
                disabled={shareCardBusy}
                className="text-[11px] px-2 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700 inline-flex items-center gap-1 disabled:opacity-50"
                data-testid="share-card-regen-btn"
              >
                {shareCardBusy ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
                {shareCardBusy ? 'Generiram…' : (form.share_card_url ? 'Regeneriraj' : 'Generiraj sada')}
              </button>
            </div>
            {form.share_card_url ? (
              <img
                src={`${BACKEND}${form.share_card_url}?ts=${Date.now()}`}
                alt="Share card preview"
                className="w-full max-w-md rounded border border-zinc-700"
                data-testid="share-card-preview"
              />
            ) : (
              <p className="text-[11px] text-zinc-500">
                Card se generira automatski pri prvoj objavi. Klikom na "Generiraj sada" možete ga napraviti odmah.
              </p>
            )}
          </div>
        )}

        {/* Category + Tags row */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-zinc-500 mb-1.5">Kategorija</label>
            <select value={form.category_id || ''} onChange={e => updateForm('category_id', e.target.value)} className="w-full bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-sm text-zinc-300 outline-none" data-testid="category-select">
              <option value="">-- Odaberi --</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name_hr}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1.5">Autor članka</label>
            <select value={form.author_id || ''} onChange={e => {
              const aid = e.target.value;
              const auth = authors.find(x => x.id === aid);
              updateForm('author_id', aid || null);
              if (auth) {
                setForm(prev => ({ ...prev, author_id: aid, author_slug: auth.slug, author_name: auth.name }));
              } else {
                setForm(prev => ({ ...prev, author_id: null, author_slug: null, author_name: 'e-mina.eu' }));
              }
            }} className="w-full bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-sm text-zinc-300 outline-none" data-testid="author-select">
              <option value="">— e-mina.eu (zadano) —</option>
              {authors.map(a => (
                <option key={a.id} value={a.id}>
                  {a.name}{a.position ? ` — ${a.position}` : (a.role ? ` — ${a.role}` : '')}
                </option>
              ))}
            </select>
            <p className="text-[10px] text-zinc-500 mt-1">
              Autor se prikazuje ispod naslova članka. Default: e-mina.eu redakcija.
            </p>
          </div>
        </div>

        <div>
            <label className="block text-xs text-zinc-500 mb-1.5">Oznake</label>
            <CreatableSelect
              isMulti
              options={tagOptions}
              value={selectedTagOptions}
              onChange={(vals) => updateForm('tag_ids', vals ? vals.map(v => v.value) : [])}
              onCreateOption={handleCreateTag}
              styles={selectStyles}
              placeholder="Odaberi ili stvori..."
              noOptionsMessage={() => 'Nema oznaka'}
              formatCreateLabel={(v) => `Stvori "${v}"`}
              classNamePrefix="rs"
              data-testid="tags-select"
            />
            <div className="mt-2 flex gap-1.5">
              <input
                type="text"
                placeholder="Ili upiši više oznaka odvojenih zarezom: sport, kultura, lokalno"
                className="flex-1 bg-zinc-900 border border-zinc-700 rounded-md px-2.5 py-1.5 text-xs text-zinc-300 outline-none placeholder-zinc-600"
                data-testid="tags-bulk-input"
                onKeyDown={async (e) => {
                  if (e.key !== 'Enter' && e.key !== ',') return;
                  if (e.key === 'Enter') e.preventDefault();
                  const raw = e.currentTarget.value;
                  const names = raw.split(',').map(s => s.trim()).filter(Boolean);
                  if (!names.length) return;
                  e.currentTarget.value = '';
                  await handleCreateManyTags(names);
                }}
              />
              <button
                type="button"
                onClick={async (ev) => {
                  const inp = ev.currentTarget.previousElementSibling;
                  if (!inp) return;
                  const raw = inp.value;
                  const names = raw.split(',').map(s => s.trim()).filter(Boolean);
                  if (!names.length) return;
                  inp.value = '';
                  await handleCreateManyTags(names);
                }}
                className="text-xs px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded border border-zinc-700"
                data-testid="tags-bulk-add-btn"
              >
                Dodaj
              </button>
            </div>
          </div>

        {/* Publish date + status row */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-zinc-500 mb-1.5 flex items-center gap-1.5">
              <Clock size={12} /> Datum i vrijeme objave
            </label>
            <input
              type="datetime-local"
              value={(() => {
                const v = form.publish_at;
                if (!v) return '';
                try {
                  const d = new Date(v);
                  if (Number.isNaN(d.getTime())) return '';
                  const pad = (n) => String(n).padStart(2, '0');
                  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
                } catch { return ''; }
              })()}
              onChange={e => updateForm('publish_at', e.target.value ? new Date(e.target.value).toISOString() : null)}
              className="w-full bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-sm text-zinc-200 outline-none focus:border-zinc-500"
              data-testid="publish-at-input"
            />
            <p className="text-[10px] text-zinc-500 mt-1">
              Promjenom datuma možete pomaknuti članak naprijed ili unazad u vremenskoj traci.
            </p>
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1.5">Status</label>
            <select
              value={form.status || 'draft'}
              onChange={e => updateForm('status', e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-sm text-zinc-300 outline-none"
              data-testid="status-select"
            >
              <option value="draft">Skica</option>
              <option value="scheduled">Zakazano</option>
              <option value="published">Objavljeno</option>
              <option value="archived">Arhivirano</option>
            </select>
          </div>
        </div>

        {/* Status + Premium row */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-xs text-zinc-500 mb-1.5">Status</label>
            <select value={form.status} onChange={e => updateForm('status', e.target.value)} className="w-full bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-sm text-zinc-300 outline-none" data-testid="status-select">
              <option value="draft">Skica</option>
              <option value="published">Objavljeno</option>
              <option value="scheduled">Zakazano</option>
              <option value="archived">Arhivirano</option>
            </select>
          </div>
          {form.status === 'scheduled' && (
            <div>
              <label className="block text-xs text-zinc-500 mb-1.5">Datum objave</label>
              <input type="datetime-local" value={form.publish_at ? form.publish_at.slice(0, 16) : ''} onChange={e => updateForm('publish_at', e.target.value ? new Date(e.target.value).toISOString() : null)} className="w-full bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-sm text-zinc-300 outline-none" data-testid="publish-at-input" />
            </div>
          )}
          <div className="flex items-end pb-1 gap-4 flex-wrap">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_premium} onChange={e => updateForm('is_premium', e.target.checked)} className="w-4 h-4 rounded border-zinc-600 bg-zinc-900" data-testid="premium-toggle" />
              <span className="text-sm text-zinc-300">Premium</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_breaking || false} onChange={e => updateForm('is_breaking', e.target.checked)} className="w-4 h-4 rounded border-zinc-600 bg-zinc-900" data-testid="breaking-toggle" />
              <span className="text-sm text-red-400">Udarna vijest</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer" title="Označi članak kao sponzoriran — bit će istaknut u 'Pročitaj još' widgetu s badge-om">
              <input type="checkbox" checked={form.is_sponsored || false} onChange={e => updateForm('is_sponsored', e.target.checked)} className="w-4 h-4 rounded border-zinc-600 bg-zinc-900" data-testid="sponsored-toggle" />
              <span className="text-sm text-amber-400">Sponzorirano</span>
            </label>
            {form.is_sponsored && (
              <>
                <input type="text" value={form.sponsored_label || ''} onChange={e => updateForm('sponsored_label', e.target.value)}
                  placeholder="Sponzorirano (oznaka)" maxLength={32}
                  className="bg-zinc-900 border border-amber-700/60 rounded-md px-3 py-1.5 text-xs text-amber-200 outline-none placeholder:text-amber-700/60 w-44"
                  data-testid="sponsored-label-input" />
                <input type="datetime-local" value={form.sponsored_until ? form.sponsored_until.slice(0, 16) : ''}
                  onChange={e => updateForm('sponsored_until', e.target.value ? new Date(e.target.value).toISOString() : null)}
                  title="Sponzorstvo aktivno do (opcionalno)"
                  className="bg-zinc-900 border border-amber-700/60 rounded-md px-3 py-1.5 text-xs text-amber-200 outline-none"
                  data-testid="sponsored-until-input" />
              </>
            )}
          </div>
        </div>

        {/* Gallery images */}
        <div>
          <label className="block text-xs text-zinc-500 mb-1.5">Izvor sadržaja</label>
          <div className="grid md:grid-cols-2 gap-2 mb-4">
            <input type="text" value={form.source_name || ''} onChange={e => updateForm('source_name', e.target.value)}
              placeholder="Naziv izvora (npr. HINA)" className="bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-sm text-zinc-300 outline-none" data-testid="source-name-input" />
            <input type="url" value={form.source_url || ''} onChange={e => updateForm('source_url', e.target.value)}
              placeholder="https://…" className="bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-sm text-zinc-300 outline-none font-mono text-xs" data-testid="source-url-input" />
          </div>
          {!form.source_name && !form.source_url && <p className="text-[10px] text-amber-400 mb-3">Preporuka: navedite izvor sadržaja.</p>}

          {/* Social media draft — admin-only, never rendered on public page.
              Used by the auto-posting pipeline. */}
          <div className="mt-4 p-3 rounded-lg border border-indigo-800/50 bg-indigo-950/20">
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs text-indigo-300 font-semibold">
                Objava za društvene mreže (samo backend)
              </label>
              <button
                type="button"
                disabled={!isEdit}
                onClick={async () => {
                  if (!isEdit) { toast.error('Prvo spremi članak'); return; }
                  toast.info('AI generira objavu za društvene mreže…');
                  try {
                    const r = await authFetch(`${API}/admin/articles/${id}/generate-social`, {
                      method: 'POST', headers: { 'Content-Type': 'application/json' },
                    });
                    if (r.ok) {
                      const d = await r.json();
                      updateForm('social_post', d.social_post || '');
                      toast.success('Objava generirana');
                    } else { toast.error('AI neuspješan'); }
                  } catch { toast.error('AI neuspješan'); }
                }}
                className="text-[11px] px-2 py-1 rounded bg-indigo-700 hover:bg-indigo-600 text-white border border-indigo-500 disabled:opacity-50"
                data-testid="generate-social-btn"
              >
                ✨ Generiraj (AI)
              </button>
            </div>
            <textarea
              value={form.social_post || ''}
              onChange={e => updateForm('social_post', e.target.value)}
              rows={4}
              maxLength={1000}
              placeholder="60–80 riječi, uključujući 1–2 hashtaga i 1–2 emojija. Automatski objavljuje na društvene mreže."
              className="w-full bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-sm text-zinc-200 outline-none focus:border-indigo-500 resize-y"
              data-testid="social-post-input"
            />
            <p className="text-[10px] text-zinc-500 mt-1">
              Riječi: {(form.social_post || '').trim().split(/\s+/).filter(Boolean).length} · Znakovi: {(form.social_post || '').length}
            </p>
          </div>

          <label className="block text-xs text-zinc-500 mb-1.5 mt-4">Galerija slika</label>
          <SortableGallery
            urls={form.gallery_image_urls || []}
            onChange={(next) => updateForm('gallery_image_urls', next)}
            onPickMedia={() => { setMediaPickerMode('gallery'); setShowMediaPicker(true); }}
          />
        </div>

        {/* Translations accordion */}
        <div className="border border-zinc-700 rounded-lg overflow-hidden">
          <button type="button" onClick={() => setShowTranslations(!showTranslations)} className="flex items-center justify-between w-full px-4 py-3 bg-zinc-800/50 hover:bg-zinc-800 text-sm text-zinc-300 transition-colors" data-testid="translations-toggle">
            <span>Prijevodi ({LANGS.length} jezika)</span>
            {showTranslations ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
          {showTranslations && (
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex gap-1 flex-wrap">
                  {LANGS.map(l => (
                    <button key={l.code} onClick={() => setActiveLang(l.code)} className={`px-2 py-1 text-xs rounded transition-colors ${activeLang === l.code ? 'bg-blue-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}>
                      {l.code.toUpperCase()}
                    </button>
                  ))}
                </div>
                <button onClick={() => toast.info('AI prijevod - uskoro!')} className="text-xs text-blue-400 hover:underline" data-testid="translate-all-btn">Prevedi sve iz hrvatskog</button>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Naslov ({activeLang.toUpperCase()})</label>
                  <input type="text" value={form.title_translations?.[activeLang] || ''} onChange={e => updateTranslation('title_translations', activeLang, e.target.value)} className="w-full bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-sm text-zinc-200 outline-none" />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Izvadak ({activeLang.toUpperCase()})</label>
                  <textarea value={form.excerpt_translations?.[activeLang] || ''} onChange={e => updateTranslation('excerpt_translations', activeLang, e.target.value)} rows={2} className="w-full bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-sm text-zinc-200 outline-none resize-y" />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Sadrzaj ({activeLang.toUpperCase()})</label>
                  <textarea value={form.body_translations?.[activeLang] || ''} onChange={e => updateTranslation('body_translations', activeLang, e.target.value)} rows={6} className="w-full bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-sm text-zinc-200 outline-none resize-y font-mono" />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showMediaPicker && (
        <MediaPickerModal
          onSelect={handleMediaSelect}
          onClose={() => setShowMediaPicker(false)}
          multiple={mediaPickerMode === 'gallery'}
          postGalleryUrls={mediaPickerMode === 'cover'
            ? [
                ...(form.gallery_image_urls || []),
                ...(form.cover_image_url ? [form.cover_image_url] : []),
              ]
            : []}
        />
      )}

      {pqOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setPqOpen(false); }}
          data-testid="pullquote-modal"
        >
          <div className="w-full max-w-2xl bg-zinc-950 border border-zinc-700 rounded-xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800 bg-zinc-900/60">
              <h3 className="text-sm font-bold text-zinc-100 inline-flex items-center gap-2">
                <Quote size={14} className="text-amber-400" /> Pull-quote prijedlozi
              </h3>
              <button onClick={() => setPqOpen(false)} className="text-zinc-400 hover:text-white" data-testid="pullquote-close-btn">
                <X size={16} />
              </button>
            </div>
            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
              {pqLoading && (
                <div className="flex items-center gap-2 text-sm text-zinc-400" data-testid="pullquote-loading">
                  <Loader2 size={14} className="animate-spin" /> AI čita članak i predlaže citate…
                </div>
              )}
              {!pqLoading && pqError && (
                <div className="p-3 rounded border border-red-800 bg-red-950/40 text-sm text-red-200" data-testid="pullquote-error">{pqError}</div>
              )}
              {!pqLoading && !pqError && pqQuotes.length === 0 && (
                <div className="text-sm text-zinc-500">Nema prijedloga.</div>
              )}
              {!pqLoading && pqQuotes.map((q, i) => (
                <div key={i} className="rounded-lg border border-zinc-800 bg-zinc-900/80 overflow-hidden" data-testid={`pullquote-item-${i}`}>
                  <div className="p-5 border-l-4 border-amber-500">
                    <div className="text-lg leading-snug text-zinc-100 font-serif italic">
                      „{q.text}”
                    </div>
                    {q.attribution && (
                      <div className="mt-3 text-[11px] uppercase tracking-wider text-amber-400 font-bold">
                        — {q.attribution}
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end gap-2 px-4 py-2 bg-zinc-950 border-t border-zinc-800">
                    <button
                      onClick={() => insertPullQuote(q)}
                      className="text-xs font-bold px-3 py-1.5 rounded bg-amber-500 hover:bg-amber-400 text-zinc-950"
                      data-testid={`pullquote-insert-${i}`}
                    >
                      Umetni u članak
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="px-5 py-3 border-t border-zinc-800 bg-zinc-900/60 text-[11px] text-zinc-500">
              Citat se ubacuje kao istaknuta <code className="text-zinc-300">&lt;figure class="em-pullquote"&gt;</code> kartica. Stil se automatski primjenjuje na javnom portalu.
            </div>
          </div>
        </div>
      )}

      {showAiEdit && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowAiEdit(false); }}
          data-testid="ai-edit-modal">
          <div className="w-full max-w-3xl bg-zinc-950 border border-zinc-700 rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800 bg-zinc-900/60">
              <h3 className="text-sm font-bold text-zinc-100">✎ AI Uredi sadržaj članka</h3>
              <button onClick={() => setShowAiEdit(false)} className="text-zinc-400 hover:text-white" data-testid="ai-edit-close"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-4 overflow-auto">
              <div className="grid md:grid-cols-2 gap-3">
                <label className="block">
                  <span className="block text-[11px] text-zinc-400 mb-1">Provider</span>
                  <select value={aiEditProvider} onChange={e => setAiEditProvider(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-zinc-900 border border-zinc-700 rounded-md text-zinc-200" data-testid="ai-edit-provider">
                    <option value="gemini">Google Gemini (besplatno)</option>
                    <option value="openai">OpenAI (Universal Key)</option>
                    <option value="anthropic">Anthropic (Universal Key)</option>
                  </select>
                </label>
                <label className="block">
                  <span className="block text-[11px] text-zinc-400 mb-1">Model</span>
                  <input value={aiEditModel} onChange={e => setAiEditModel(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-zinc-900 border border-zinc-700 rounded-md text-zinc-200" data-testid="ai-edit-model"
                    placeholder="gemini-2.5-flash" />
                  <span className="text-[10px] text-zinc-500 mt-1 block">Default: gemini-2.5-flash (besplatno).</span>
                </label>
              </div>
              <label className="block">
                <span className="block text-[11px] text-zinc-400 mb-1">Sustavni prompt (opcionalno — prazno = default urednički prompt)</span>
                <textarea value={aiEditSystem} onChange={e => setAiEditSystem(e.target.value)} rows={3}
                  className="w-full px-3 py-2 text-sm bg-zinc-900 border border-zinc-700 rounded-md text-zinc-200 font-mono"
                  placeholder="Npr. Skrati za 30%, pojačaj prvi paragraf, dodaj 2 H2 podnaslova…"
                  data-testid="ai-edit-system" />
              </label>
              <label className="block">
                <span className="block text-[11px] text-zinc-400 mb-1">Uputa (što napraviti) *</span>
                <textarea value={aiEditInstruction} onChange={e => setAiEditInstruction(e.target.value)} rows={4}
                  className="w-full px-3 py-2 text-sm bg-zinc-900 border border-zinc-700 rounded-md text-zinc-200"
                  placeholder="npr. Preformuliraj prvi paragraf, dodaj jasniji uvod, izbaci ponavljanja, prevedi citate na hrvatski."
                  data-testid="ai-edit-instruction" required />
              </label>
              {aiEditPreview != null && (
                <div className="border border-emerald-700/50 rounded-lg overflow-hidden">
                  <div className="px-3 py-2 bg-emerald-900/30 text-[11px] uppercase tracking-wider text-emerald-200 font-bold">Pregled rezultata</div>
                  <div className="p-3 max-h-72 overflow-auto prose prose-invert prose-sm" dangerouslySetInnerHTML={{ __html: aiEditPreview }} data-testid="ai-edit-preview" />
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-zinc-800 bg-zinc-900/60">
              <button onClick={() => setShowAiEdit(false)} className="text-xs px-3 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300" data-testid="ai-edit-cancel">Odustani</button>
              <button onClick={runAiEditBody} disabled={aiEditBusy || !aiEditInstruction.trim()}
                className="text-xs px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:opacity-50 inline-flex items-center gap-1"
                data-testid="ai-edit-run">
                {aiEditBusy ? <Loader2 size={12} className="animate-spin" /> : '✨'} Pokreni AI
              </button>
              {aiEditPreview != null && (
                <button onClick={applyAiEdit} className="text-xs px-3 py-1.5 rounded bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold" data-testid="ai-edit-apply">
                  Primijeni
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
